import { useState } from "react";
import {
  Building2,
  Info,
  Radio,
  SlidersHorizontal,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { SegmentStrip } from "../components/data/SegmentStrip";
import { DispatchSettingsTab } from "../components/settings/DispatchSettingsTab";
import { DeliveryFeesSettingsTab } from "../components/settings/DeliveryFeesSettingsTab";
import {
  PreferencesSettingsTab,
} from "../components/settings/PreferencesSettingsTab";
import {
  ProfileSettingsSkeleton,
  ProfileSettingsTab,
} from "../components/settings/ProfileSettingsTab";
import { SystemInfoTab } from "../components/settings/SystemInfoTab";
import { usePartnerProfile } from "../hooks/usePartnerSettings";
import { useI18n, type TranslationKey } from "../i18n";

type SettingsSegment = "profile" | "dispatch" | "fees" | "preferences" | "system";

const SEGMENT_META: {
  key: SettingsSegment;
  labelKey: TranslationKey;
  icon: LucideIcon;
}[] = [
  { key: "profile", labelKey: "settings.tabs.profile", icon: Building2 },
  { key: "dispatch", labelKey: "settings.tabs.dispatch", icon: Radio },
  { key: "fees", labelKey: "settings.tabs.fees", icon: Truck },
  {
    key: "preferences",
    labelKey: "settings.tabs.preferences",
    icon: SlidersHorizontal,
  },
  { key: "system", labelKey: "settings.tabs.system", icon: Info },
];

export default function SettingsPage() {
  const { t } = useI18n();
  const [segment, setSegment] = useState<SettingsSegment>("profile");
  const profileQuery = usePartnerProfile();
  const profile = profileQuery.data;

  const segments = SEGMENT_META.map((s) => ({
    key: s.key,
    label: t(s.labelKey),
    icon: s.icon,
  }));

  return (
    <div className="menu-page">
      <header className="menu-page__header">
        <div className="min-w-0">
          <p className="menu-page__eyebrow">{t("settings.eyebrow")}</p>
          <h1 className="menu-page__title">{t("settings.title")}</h1>
          <p className="menu-page__subtitle">{t("settings.subtitle")}</p>
        </div>
      </header>

      <SegmentStrip
        value={segment}
        onChange={setSegment}
        ariaLabel={t("settings.segmentsAria")}
        options={segments}
      />

      {profileQuery.isLoading || !profile ? (
        <ProfileSettingsSkeleton />
      ) : profileQuery.isError ? (
        <section className="menu-panel">
          <div className="menu-panel__body">
            <p className="text-sm text-[var(--danger)]">{t("settings.loadError")}</p>
          </div>
        </section>
      ) : (
        <>
          {segment === "profile" ? <ProfileSettingsTab profile={profile} /> : null}
          {segment === "dispatch" ? (
            <DispatchSettingsTab profile={profile} />
          ) : null}
          {segment === "fees" ? <DeliveryFeesSettingsTab /> : null}
          {segment === "preferences" ? <PreferencesSettingsTab /> : null}
          {segment === "system" ? <SystemInfoTab profile={profile} /> : null}
        </>
      )}
    </div>
  );
}
